import React, { useState, useEffect } from 'react';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import { trackPageView } from '../utils/analytics';
import SEO from '../components/SEO';
import PopupForm from '../components/PopupForm';
import F2FHero from '../components/fresher2fullstack/F2FHero';
import F2FRoadmap from '../components/fresher2fullstack/F2FRoadmap';
import F2FWhoIsItFor from '../components/fresher2fullstack/F2FWhoIsItFor';
import F2FTechStack from '../components/fresher2fullstack/F2FTechStack';
import F2FInclusions from '../components/fresher2fullstack/F2FInclusions';
import F2FPricing from '../components/fresher2fullstack/F2FPricing';
import F2FFAQ from '../components/fresher2fullstack/F2FFAQ';
import F2FCTA from '../components/fresher2fullstack/F2FCTA';
import { API_ENDPOINTS } from '../config/constants';
import '../styles/fresher2fullstack.css';
import '../styles/architect.css';
import '../styles/benefits.css';
import '../styles/PopupForm.css';
import '../styles/pricing.css';
import '../styles/faq.css';
import '../styles/cta-final.css';
import '../components/home/HomePage.css';

// Mobile top-bar lead form (mirrors pattern from ArchitectProgram.js)
function TopBarLeadForm({ visible, onSubmit, onClose, submitting }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !mobile.trim() || !consent) {
      setError('Please fill all fields and accept the consent.');
      return;
    }
    setError('');
    onSubmit({ name, email, mobile, consent });
  };

  if (!visible) return null;
  return (
    <div className="topbar-lead-form">
      <div className="topbar-lead-form-cta">Register for the Fresher to Full Stack program</div>
      <form onSubmit={handleSubmit} className="topbar-lead-form-inner">
        <input
          type="text"
          placeholder="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={submitting}
          required
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={submitting}
          required
        />
        <input
          type="tel"
          placeholder="Mobile"
          value={mobile}
          onChange={e => setMobile(e.target.value)}
          disabled={submitting}
          required
          pattern="[0-9]{10,15}"
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
          <input
            type="checkbox"
            id="topbar-consent-f2f"
            checked={consent}
            onChange={e => setConsent(e.target.checked)}
            disabled={submitting}
            required
          />
          <label htmlFor="topbar-consent-f2f" style={{ cursor: 'pointer' }}>
            I allow to send me regular updates
          </label>
        </div>
        <button type="submit" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Get Info'}
        </button>
        <button
          type="button"
          className="topbar-lead-form-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </form>
      {error && <div className="topbar-lead-form-error">{error}</div>}
    </div>
  );
}

export default function Fresher2Fullstack() {
  const [showTopBarForm, setShowTopBarForm] = useState(false);
  const [topBarSubmitting, setTopBarSubmitting] = useState(false);
  const [topBarSubmitted, setTopBarSubmitted] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [formType, setFormType] = useState('');
  const [userLocation, setUserLocation] = useState('IN');

  // Scroll-driven animation state
  const [visiblePhases, setVisiblePhases] = useState([]);
  const [visibleWhoCards, setVisibleWhoCards] = useState([]);

  const pricing = userLocation === 'US'
    ? { current: '$10', original: '$25' }
    : { current: '₹999', original: '₹1499' };

  /* ── lifecycle ── */
  useEffect(() => {
    window.scrollTo(0, 0);
    const storedLocation = sessionStorage.getItem('loc');
    if (storedLocation) setUserLocation(storedLocation);
  }, []);

  useEffect(() => {
    trackPageView('Fresher to Full Stack', 'Program Details');
  }, []);

  /* ── mobile top-bar scroll trigger ── */
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY || document.documentElement.scrollTop;
      const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (pageHeight > 0 && scrolled > pageHeight * 0.10) {
        if (window.innerWidth <= 900) setShowTopBarForm(true);
      } else {
        setShowTopBarForm(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* ── intersection observers ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.phaseindex);
            setVisiblePhases((prev) => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('.f2f-phase-item').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.cardindex);
            setVisibleWhoCards((prev) => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.2 }
    );
    document.querySelectorAll('.who-card-item').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /* ── handlers ── */
  const openPopup = (type) => {
    setFormType(type);
    setIsPopupOpen(true);
  };

  const closePopup = () => setIsPopupOpen(false);

  const handleTopBarFormSubmit = async ({ name, email, mobile, consent }) => {
    setTopBarSubmitting(true);
    try {
      let token = localStorage.getItem('token');
      if (!token) {
        token = generateToken();
        localStorage.setItem('token', token);
      }
      const headers = {
        'Content-Type': 'application/json',
        [TOKEN_HEADER]: token,
      };
      const response = await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name,
          email,
          mobile,
          consent,
          source: 'f2f_topbar',
          interestedIn: 'fresher_to_fullstack',
        }),
      });
      if (!response.ok) throw new Error('Failed to submit');
      setTopBarSubmitted(true);
      setTimeout(() => {
        setShowTopBarForm(false);
        setTopBarSubmitted(false);
      }, 2500);
    } catch {
      alert('Failed to submit. Please try again.');
    } finally {
      setTopBarSubmitting(false);
    }
  };

  return (
    <>
    <TopBarLeadForm
      visible={showTopBarForm && !topBarSubmitted}
      onSubmit={handleTopBarFormSubmit}
      onClose={() => setShowTopBarForm(false)}
      submitting={topBarSubmitting}
    />
    {topBarSubmitted && showTopBarForm && (
      <div className="topbar-lead-form-success">Thank you! We'll be in touch soon.</div>
    )}
    <div className="nh-home architect-page f2f-page">

      <SEO pageName="fresher2Fullstack" />

      {/* Hero */}
      <F2FHero
        onCurriculum={() => openPopup('curriculum')}
        pricing={pricing}
      />

      {/* What you'll walk away with */}
      <section className="f2f-walkaway">
        <div className="wrap f2f-walkaway-inner">
          <div className="f2f-walkaway-left">
            <p className="f2f-walkaway-eyebrow">What you'll walk away with</p>
            <h2 className="f2f-walkaway-heading">A real plan for your first 100 weeks — not motivation.</h2>
            <p className="f2f-walkaway-sub">
              By the end of the session you'll know exactly what to learn, in what order, and how to
              use AI to move faster than the people around you.
            </p>
          </div>
          <ul className="f2f-walkaway-list">
            {[
              'A week-by-week skill roadmap for your first ~2 years in tech',
              'The full-stack foundations that still matter — and the ones that don\'t',
              'How to use AI as a force multiplier instead of being replaced by it',
              'A realistic plan to go from "just hired" to "genuinely valuable" fast',
              'Clarity on what employers actually look for in 2026 and beyond',
            ].map((item, i) => (
              <li key={i} className="f2f-walkaway-item">
                <span className="f2f-walkaway-check" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <circle cx="9" cy="9" r="9" fill="rgba(40,200,120,0.15)" />
                    <path d="M5 9.5l2.8 2.8L13 6" stroke="#3ee09a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Roadmap */}
      <F2FRoadmap />

      {/* Agenda */}
      <section className="f2f-agenda">
        <div className="wrap">
          <p className="f2f-agenda-eyebrow">How the session runs</p>
          <h2 className="f2f-agenda-heading">Four focused hours. Zero filler.</h2>
          <div className="f2f-agenda-list">
            {[
              { time: '10:00 AM', title: 'The ground has shifted',        desc: 'How AI rewrote the rules for new developers — and what that means for your first job.' },
              { time: '10:40 AM', title: 'Foundations that still matter', desc: 'The first 12 weeks: what to learn first, and the popular advice you can ignore.' },
              { time: '11:30 AM', title: 'Full-stack, mapped',            desc: 'The skill order from front end to back end to deployment — without the overwhelm.' },
              { time: '12:15 PM', title: 'AI as a force multiplier',      desc: 'Concrete ways to use AI to learn and ship faster instead of being replaced by it.' },
              { time: '1:00 PM',  title: 'From "just hired" to valuable', desc: 'What employers actually reward in 2026 and beyond, and how to get there fast.' },
              { time: '1:40 PM',  title: 'Your plan + live Q&A',          desc: 'Put it together into your own roadmap and bring your questions to Chai.' },
            ].map((item) => (
              <div key={item.time} className="f2f-agenda-row">
                <span className="f2f-agenda-time">{item.time}</span>
                <div className="f2f-agenda-content">
                  <p className="f2f-agenda-title">{item.title}</p>
                  <p className="f2f-agenda-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Is It For */}
      <F2FWhoIsItFor />

      {/* Pricing */}
      <F2FPricing
        pricing={pricing}
      />

      {/* FAQ */}
      <F2FFAQ />

      {/* Final CTA */}
      <F2FCTA
      />

    </div>
    {/* Lead capture popup */}
    {isPopupOpen && (
      <PopupForm
        isOpen={isPopupOpen}
        onClose={closePopup}
        formType={formType}
        programName="fresher_to_fullstack"
        source="f2f_page"
        pricing={pricing}
      />
    )}
    </>
  );
}
